import React from 'react'
import { Box, Grid, Text, Stack, Checkbox } from '@chakra-ui/react'
import { Controller, Control, FieldErrors } from 'react-hook-form'
import { DashboardZones, UsersPermissionsEntity } from '@/entities/api/users/users'

const ModuleNames: Record<DashboardZones, string> = {
	[DashboardZones.orders]: 'Órdenes',
	[DashboardZones.inventary]: 'Inventario',
	[DashboardZones.users]: 'Usuarios',
	[DashboardZones.customers]: 'Clientes',
	[DashboardZones.contact]: 'Contacto',
	[DashboardZones.categories]: 'Categorías',
	[DashboardZones.coupons]: 'Cupones',
	[DashboardZones.sales]: 'Ventas'
}

interface PermissionsZoneProps {
	control: Control<any>
	errors: FieldErrors<any>
	showPermissions: boolean
	isEdit?: boolean
	permissions?: UsersPermissionsEntity[]
}

const PermissionsZone: React.FC<PermissionsZoneProps> = ({
	control,
	errors,
	showPermissions,
	isEdit = false,
	permissions = [],
}) => {
	const getInitialPermissions = (): UsersPermissionsEntity[] => {
		if (isEdit && permissions.length > 0) {
			const existingPermissionsMap = permissions.reduce((acc, perm) => {
				acc[perm.module] = perm
				return acc
			}, {} as Record<DashboardZones, UsersPermissionsEntity>)

			return Object.values(DashboardZones).map((module) => {
				return (
					existingPermissionsMap[module] || {
						module,
						add: false,
						view: false,
						edit: false,
						remove: false,
					}
				)
			})
		}

		return Object.values(DashboardZones).map((module) => ({
			module,
			add: true,
			view: true,
			edit: true,
			remove: true,
		}))
	}

	if (!showPermissions) {
		return null
	}

	return (
		<Box w='full' mb='6'>
			<Controller
				name='permissions'
				control={control}
				defaultValue={getInitialPermissions()}
				render={({ field: { onChange, value } }) => {
					const currentPermissions = value || getInitialPermissions()

					const updatePermission = (
						moduleIndex: number,
						permissionType: keyof Omit<UsersPermissionsEntity, 'module'>,
						checked: boolean
					) => {
						const newPermissions = [...currentPermissions]
						newPermissions[moduleIndex] = {
							...newPermissions[moduleIndex],
							[permissionType]: checked,
						}
						onChange(newPermissions)
					}

					return (
						<Stack gap={4} align='stretch'>
							<Grid
								templateColumns='200px repeat(4, 1fr) 80px'
								gap={4}
								alignItems='center'
								py={2}
								px={2}
								bg='gray.100'
								borderRadius='md'
								fontSize='sm'
								fontWeight='medium'
							>
								<Text>Módulo</Text>
								<Text textAlign='center'>Ver</Text>
								<Text textAlign='center'>Agregar</Text>
								<Text textAlign='center'>Editar</Text>
								<Text textAlign='center'>Eliminar</Text>
							</Grid>

							{currentPermissions.map(
								(permission: UsersPermissionsEntity, moduleIndex: number) => {
									return (
										<Box key={permission.module}>
											<Grid
												templateColumns='200px repeat(4, 1fr) 80px'
												gap={4}
												alignItems='center'
												py={2}
												_hover={{ bg: 'gray.50' }}
												borderRadius='md'
												px={2}
											>
												<Text fontSize='sm' fontWeight='medium'>
													{ModuleNames[permission.module]}
												</Text>

												<Box display='flex' justifyContent='center'>
													<Checkbox.Root
														checked={permission.view}
														colorPalette='black'
														onCheckedChange={(details) =>
															updatePermission(
																moduleIndex,
																'view',
																details.checked === true
															)
														}
													>
														<Checkbox.HiddenInput />
														<Checkbox.Control>
															<Checkbox.Indicator />
														</Checkbox.Control>
													</Checkbox.Root>
												</Box>

												<Box display='flex' justifyContent='center'>
													<Checkbox.Root
														checked={permission.add}
														colorPalette='black'
														onCheckedChange={(details) =>
															updatePermission(
																moduleIndex,
																'add',
																details.checked === true
															)
														}
													>
														<Checkbox.HiddenInput />
														<Checkbox.Control>
															<Checkbox.Indicator />
														</Checkbox.Control>
													</Checkbox.Root>
												</Box>

												<Box display='flex' justifyContent='center'>
													<Checkbox.Root
														checked={permission.edit}
														colorPalette='black'
														onCheckedChange={(details) =>
															updatePermission(
																moduleIndex,
																'edit',
																details.checked === true
															)
														}
													>
														<Checkbox.HiddenInput />
														<Checkbox.Control>
															<Checkbox.Indicator />
														</Checkbox.Control>
													</Checkbox.Root>
												</Box>

												<Box display='flex' justifyContent='center'>
													<Checkbox.Root
														checked={permission.remove}
														colorPalette='black'
														onCheckedChange={(details) =>
															updatePermission(
																moduleIndex,
																'remove',
																details.checked === true
															)
														}
													>
														<Checkbox.HiddenInput />
														<Checkbox.Control>
															<Checkbox.Indicator />
														</Checkbox.Control>
													</Checkbox.Root>
												</Box>
											</Grid>
										</Box>
									)
								}
							)}

							{errors.permissions && (
								<Text color='red.500' fontSize='sm'>
									{typeof errors.permissions.message === 'string'
										? errors.permissions.message
										: 'Error en permisos'}
								</Text>
							)}
						</Stack>
					)
				}}
			/>
		</Box>
	)
}

export default PermissionsZone
